/**
 * Shared workforce presentation helpers — 13-ui-screens.md §9 and §10.
 *
 * WHERE THIS LIVES AND WHY. It sits inside a route folder, prefixed `-` so the
 * TanStack file-route generator ignores it. It is used by exactly two screens,
 * both owned by the same task — `/_app/workers/**` and `/_app/documents` — so
 * it is deliberately NOT a 17th entry in `components/primitives`. Nothing
 * outside those two folders imports it.
 *
 * Everything here is presentation only. No band, no expiry state and no
 * document state is COMPUTED here: those all arrive already decided by the API
 * (12-api-contract.md §5.4 — "computed server-side so the 45-days-is-in-the
 * -60-day-band acceptance criterion has exactly one implementation").
 */
import type { CSSProperties } from 'react';
import { PAY_SCHEME_LABEL, sortPaySchemes } from '@cropmate/shared';
import type { DocumentExpiryBand, PayScheme, WorkerStatus } from '@cropmate/shared';
import type { BandTone, PillKind } from '@/components/primitives';

/* ═════════════════════════════ nationality ══════════════════════════════ */

/**
 * The API stores nationality as an ISO 3166-1 alpha-2 code (`MY`, `BD`, `NP`,
 * `ID`, `VN` in the seed) and there is **no nationality catalogue endpoint** —
 * verified against the running API: `GET /meta/enums` returns twelve keys and
 * `nationality` is not one of them. §9.3 requires the full country name in the
 * `NAT` column, so the display map lives here. An unknown code falls through
 * to itself rather than to a wrong guess.
 */
export const NATIONALITY_NAMES: Readonly<Record<string, string>> = {
  MY: 'Malaysia',
  ID: 'Indonesia',
  BD: 'Bangladesh',
  NP: 'Nepal',
  VN: 'Vietnam',
  MM: 'Myanmar',
  IN: 'India',
  PK: 'Pakistan',
  LK: 'Sri Lanka',
  PH: 'Philippines',
  TH: 'Thailand',
  KH: 'Cambodia',
  LA: 'Laos',
  CN: 'China',
  SG: 'Singapore',
};

export function nationalityLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return NATIONALITY_NAMES[code.toUpperCase()] ?? code;
}

/* ═══════════════════════════════ pay scheme ═════════════════════════════ */

/**
 * §9.3 scheme chip. OQ-7 is explicit: **never** derive the scheme by parsing a
 * `W-` / `C-` employee-id prefix. The engagements are their own effective-dated
 * records and they are what this reads.
 *
 * A SET, not a value, since migration 0016: a worker may hold piece-rate and
 * casual terms at the same time, which at this plant is ordinary rather than
 * exceptional. The labels and the ordering come from `@cropmate/shared` so this
 * screen, the payslip and the register cannot drift apart on them.
 */
export { PAY_SCHEME_LABEL };

const CHIP_BASE: CSSProperties = {
  display: 'inline-block',
  borderRadius: 3,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  borderWidth: 1,
  borderStyle: 'solid',
};

const CASUAL_TONE: CSSProperties = {
  background: 'var(--earth-100)',
  color: 'var(--earth-700)',
  borderColor: 'var(--earth-300)',
};

const PIECE_TONE: CSSProperties = {
  background: 'var(--neutral-100)',
  color: 'var(--neutral-700)',
  borderColor: 'var(--border-subtle)',
};

const CHIP_TONE: Readonly<Record<PayScheme, CSSProperties>> = {
  CASUAL_TIME: CASUAL_TONE,
  PIECE_RATE: PIECE_TONE,
};

/**
 * ONE chip per scheme, not one chip reading `Piece-rate + Casual`.
 *
 * The register is scanned down a column, and a single wide chip whose text
 * changes length row to row breaks that scan. Two narrow chips keep each scheme
 * in the same visual slot whether or not the worker also holds the other, and
 * they keep the existing colour coding — the earth tone still means "casual"
 * wherever it appears. `paySchemesLabel()` is the prose form, used where a
 * sentence is wanted instead (the profile fact, reconciliation causes).
 */
export function SchemeChip({ schemes }: { schemes: readonly PayScheme[] }) {
  const ordered = sortPaySchemes(schemes);
  if (ordered.length === 0) {
    return <span style={{ color: 'var(--neutral-400)' }}>—</span>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {ordered.map((scheme) => (
        <span key={scheme} style={{ ...CHIP_BASE, ...CHIP_TONE[scheme] }}>
          {PAY_SCHEME_LABEL[scheme]}
        </span>
      ))}
    </span>
  );
}

/* ═══════════════════════════ worker status ══════════════════════════════ */

/**
 * §9.3: `Active` gets the `Valid` pill styling; anything else gets `Pending`
 * pill styling. The labels come from `GET /meta/enums → workerStatus`, so the
 * fallback map below is only used before that query settles.
 */
export const WORKER_STATUS_FALLBACK: Readonly<Record<WorkerStatus, string>> = {
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  TERMINATED: 'Terminated',
};

export function workerStatusPill(status: WorkerStatus): PillKind {
  return status === 'ACTIVE' ? 'valid' : 'pending';
}

/* ═══════════════════════ document expiry bands ══════════════════════════ */

/**
 * Band → pill colour. The band itself is the API's (`EXPIRED | DUE_30 |
 * DUE_60 | DUE_90 | VALID`); this only picks a colour for it.
 *
 * §10.3: "Delta pill kind by band: EXPIRED → Expired; ≤ 30 → Soon; ≤ 60 and
 * ≤ 90 → Valid."
 */
export function bandPillKind(band: DocumentExpiryBand): PillKind {
  if (band === 'EXPIRED') return 'expired';
  if (band === 'DUE_30') return 'soon';
  return 'valid';
}

/** §9.3 `DOCS` column — one word, the worst state across the worker's papers. */
export function bandShortLabel(band: DocumentExpiryBand): string {
  if (band === 'EXPIRED') return 'Expired';
  if (band === 'DUE_30') return 'Soon';
  return 'Valid';
}

/**
 * §9.4 documents section — `Expired 20 days` / `In 27 days` / `Valid`.
 * `daysToExpiry` is the API's own figure and is negative once expired.
 */
export function bandLongLabel(band: DocumentExpiryBand, daysToExpiry: number): string {
  if (band === 'EXPIRED') {
    const n = Math.abs(daysToExpiry);
    return `Expired ${n} ${n === 1 ? 'day' : 'days'}`;
  }
  if (band === 'DUE_30') {
    if (daysToExpiry === 0) return 'Expires today';
    return `In ${daysToExpiry} ${daysToExpiry === 1 ? 'day' : 'days'}`;
  }
  return 'Valid';
}

/**
 * §10.3 delta text — `28 days ago` for expired, `in 11 days` for future.
 * Driven by the API's `daysToExpiry`, never by a local date subtraction, so
 * the text can never disagree with the band it is sitting in.
 */
export function deltaText(daysToExpiry: number): string {
  if (daysToExpiry === 0) return 'today';
  if (daysToExpiry > 0) {
    return `in ${daysToExpiry} ${daysToExpiry === 1 ? 'day' : 'days'}`;
  }
  const ago = Math.abs(daysToExpiry);
  return `${ago} ${ago === 1 ? 'day' : 'days'} ago`;
}

/* ════════════════════════ the band table itself ═════════════════════════ */

/**
 * The four bands the expiry screen draws, in the API's own vocabulary.
 * `VALID` is a real band on the wire but never gets a card or a section —
 * a valid document is not something to action.
 */
export type ExpiryScreenBand = Exclude<DocumentExpiryBand, 'VALID'>;

/** Those four plus §10.3 [N]'s fifth band, which has no expiry to be banded by. */
export type ScreenBand = ExpiryScreenBand | 'MISSING';

export const BAND_ORDER: readonly ScreenBand[] = [
  'EXPIRED',
  'DUE_30',
  'DUE_60',
  'DUE_90',
  'MISSING',
];

/**
 * §10.3 header labels, verbatim from the prototype — NOT the API's prose
 * `label` ("Expiring in 30–60 days"), which is shown as the header's title
 * attribute instead.
 */
export const BAND_LABEL: Readonly<Record<ScreenBand, string>> = {
  EXPIRED: 'EXPIRED',
  DUE_30: '≤ 30 DAYS',
  DUE_60: '≤ 60 DAYS',
  DUE_90: '≤ 90 DAYS',
  MISSING: 'NO DOCUMENT ON FILE',
};

/**
 * The exclusive interval each band covers, spelled out in the header so the
 * FR-2.4 acceptance criterion is legible on screen rather than only in a test:
 * a document expiring in 45 days is in `≤ 60 DAYS`, never in `≤ 30 DAYS`.
 * Mirrors 12-api-contract.md §5.4's half-open definition exactly.
 */
export const BAND_RANGE: Readonly<Record<ScreenBand, string>> = {
  EXPIRED: 'already expired',
  DUE_30: '0–30 days',
  DUE_60: '31–60 days',
  DUE_90: '61–90 days',
  MISSING: 'no expiry to band',
};

/** §10.3 right-hand action text, verbatim. */
export const BAND_ACTION: Readonly<Record<ScreenBand, string>> = {
  EXPIRED: 'Work must stop today',
  DUE_30: 'File renewal with agent',
  DUE_60: 'Agent notified',
  DUE_90: 'Monitor',
  MISSING: 'Collect from agent',
};

export const BAND_TONE: Readonly<Record<ScreenBand, BandTone>> = {
  EXPIRED: 'expired',
  DUE_30: 'due30',
  DUE_60: 'due60',
  DUE_90: 'due90',
  MISSING: 'missing',
};

/** §10.2 summary-card copy. Only the four expiry bands get a card. */
export const CARD_NOTE: Readonly<Record<ExpiryScreenBand, string>> = {
  EXPIRED: 'Work stopped until renewed',
  DUE_30: 'Renewal must be filed now',
  DUE_60: 'Agent notified',
  DUE_90: 'Monitor',
};

/** `n workers` / `1 worker` — the mono count slot in a band header. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
